import { useRef, useEffect } from "react";
import styled from "styled-components";
import { AnimatePresence, motion } from "motion/react";

import useWizard from "./useWizard";
import WizardIntro from "./WizardIntro";
import WizardOverview from "./WizardOverview";
import WizardNav from "./WizardNav";
import WizardNavigation from "./WizardNavigation";
import WizardStepAnimator from "./WizardStepAnimator";

export default function WizardOverlay() {
  const { currentStep, currentSubStep, showOverview, closeWizard, collapseOverlay, collapsed } = useWizard();

  // Track whether the wizard layout is entering from intro/overview (animate)
  // vs mounting directly into a step (no content slide)
  const cameFromIntroRef = useRef(currentStep === 0);
  useEffect(() => {
    if (currentStep === 0) cameFromIntroRef.current = true;
  }, [currentStep]);

  const isIntro = currentStep === 0 && !showOverview;
  const isOverview = currentStep === 0 && showOverview;
  const isFullScreen = currentStep === 5 && currentSubStep >= 1;
  const showCollapseBtn = currentStep === 5 && currentSubStep === 2 && !collapsed;
  const isWizard = currentStep >= 1 && !isFullScreen;

  return (
    <Overlay>
      <CloseButton onClick={closeWizard} $collapsed={collapsed}>
        <i className="fas fa-circle-xmark" />
      </CloseButton>
      {showCollapseBtn && (
        <CollapseButton onClick={collapseOverlay}>
          <i className="fas fa-circle-chevron-right" />
        </CollapseButton>
      )}
      <AnimatePresence mode="wait">
        {isIntro && (
          <FullWrapper
            key="intro"
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: -60 }}
            transition={{ duration: 0.3 }}
          >
            <WizardIntro />
          </FullWrapper>
        )}
        {isOverview && (
          <FullWrapper
            key="overview"
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, x: -60 }}
            transition={{ duration: 0.3 }}
          >
            <WizardOverview />
          </FullWrapper>
        )}
        {isFullScreen && (
          <FullWrapper
            key="fullscreen"
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <ScrollArea>
              <WizardStepAnimator />
            </ScrollArea>
          </FullWrapper>
        )}
        {isWizard && (
          <WizardLayout
            key="wizard"
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <WizardNav />
            <ContentArea
              as={motion.div}
              initial={cameFromIntroRef.current ? { opacity: 0, x: 40 } : false}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.3 }}
            >
              <ScrollArea>
                <WizardStepAnimator />
              </ScrollArea>
              <WizardNavigation />
            </ContentArea>
          </WizardLayout>
        )}
      </AnimatePresence>
    </Overlay>
  );
}

const Overlay = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  background: linear-gradient(135deg, #f9f9fb 0%, #f0f0f3 100%);
  border-radius: 32px;
  position: relative;
`;

const FullWrapper = styled.div`
  flex: 1;
  display: flex;
`;

const WizardLayout = styled.div`
  flex: 1;
  display: flex;
`;

const ContentArea = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 40px;
  display: flex;
  flex-direction: column;
`;

const CollapseButton = styled.button`
  appearance: none;
  border: none;
  background: none;
  position: absolute;
  top: 14px;
  right: 12px;
  padding: 0;
  cursor: pointer;
  color: #c4c4c4;
  font-size: 32px;
  line-height: 1;
  z-index: 10;

  transition: color 0.2s, transform 0.15s;

  &:hover {
    color: #888;
  }

  &:active {
    transform: scale(0.9);
    color: #666;
  }
`;

const CloseButton = styled.button`
  appearance: none;
  border: none;
  background: none;
  position: absolute;
  top: 14px;
  left: 12px;
  padding: 0;
  cursor: pointer;
  color: #c4c4c4;
  font-size: 32px;
  line-height: 1;
  z-index: 10;
  opacity: ${(p) => (p.$collapsed ? 0 : 1)};
  pointer-events: ${(p) => (p.$collapsed ? "none" : "auto")};

  transition: color 0.2s, transform 0.15s, opacity 0.3s;

  &:hover {
    color: #888;
  }

  &:active {
    transform: scale(0.9);
    color: #666;
  }
`;
